/**
 * The TokenDetail container
 */
import React, { useState, useEffect } from "react";
import { useSsrEffect } from "@issr/core";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Container, Button, Badge, Row, Col } from "reactstrap";
import { useWeb3React } from "@web3-react/core";
import {
  showUnlockableContent,
  showUnlockableContentErrorReset,
  getUnlockableContentAttributes,
  getUnlockableContentRelationships,
  prepareUnlockUnlockableContent,
  unlockUnlockableContent,
} from "../../redux/unlockableContents";
import { getContentCategoryAttributes } from "../../redux/contentCategories";
import { getProjectAttributes } from "../../redux/projects";
import ScrollToTop from "../../components/Utils/Scroll/ScrollToTop";
import Page from "../../components/Page";
import ErrorModal from "../../components/ErrorModal";
import UnlockTokenSelector from "../../components/UnlockTokenSelector";
import Medium from "../../components/Medium";
import {
  REACT_APP_PROJECT_ID,
  REACT_APP_UNLOCKABLE_CONTENT_ID,
} from "../../utils/env";
import SuccessModal from "../../components/SuccessModal";
import ReactPlayer from "react-player";

/**
 * TokenDetail container used as app default landing page
 */
const UnlockableContentDetail = (props, context) => {
  const { contentId } = useParams();
  const dispatch = useDispatch();
  const [tokenSelectorOpen, toggleTokenSelector] = useState(false);

  const unlockableContent = useSelector((state) =>
    getUnlockableContentAttributes(state, contentId)
  );

  const showId = useSelector((state) => state?.unlockableContents?.showId?.id);
  const dispatchApi = async () => {
    if (showId != contentId) {
      dispatch(
        showUnlockableContent({
          projectId: REACT_APP_PROJECT_ID,
          id: contentId,
        })
      );
    } else {
      // TODO maybe move those checks to another place? also we should maybe check for renewing after X minutes or so
      console.log(
        "[DEBUG] Did not fire API requests since data is already loaded",
        unlockableContent?.title
      );
    }
  };

  useSsrEffect(dispatchApi);
  useEffect(dispatchApi, [dispatch, contentId]);

  const projectData = useSelector((state) =>
    getProjectAttributes(state, REACT_APP_PROJECT_ID)
  );
  const unlockableContentRelationships = useSelector((state) =>
    getUnlockableContentRelationships(state, contentId)
  );
  const contentCategory = useSelector((state) =>
    getContentCategoryAttributes(
      state,
      unlockableContentRelationships?.content_category?.data?.id
    )
  );
  const unlockError = useSelector(
    (state) => state?.unlockableContents?.unlockError
  );
  const unlockSuccess = useSelector(
    (state) => state?.unlockableContents?.unlockSuccess
  );
  const error = useSelector((state) => state?.unlockableContents?.showError);

  const { account, provider, connector } = useWeb3React();

  const unlockContent = (tokens, unlockInputsValues = undefined) => {
    if (unlockableContent) {
      dispatch(
        prepareUnlockUnlockableContent({
          account,
          connector,
          provider,
          title: unlockableContent?.title,
          project_title: projectData?.title,
          // unlock_boundary_id: 1, // If only one unlock boundary exists we ignore this, if multiple we check which one matches
          token_identifier: tokens[0].identifier,
          contract_address: tokens[0].contract_address,
          callbackSuccess: ({
            signature,
            token_identifier,
            contract_address,
            unlock_boundary_id,
          }) => {
            dispatch(
              unlockUnlockableContent({
                signature,
                token_identifier,
                contract_address,
                id: contentId,
                project_id: REACT_APP_PROJECT_ID,
                unlock_boundary_id,
                unlock_input_values: unlockInputsValues,
                callbackSuccess: () => {
                  console.log("Successfully unlocked");
                },
              })
            );
          },
        })
      );
    }
  };

  const unlockableContentName =
    unlockableContent?.title || context.t("Loading Content...");

  let unlockInputIdsArray = [];

  unlockableContentRelationships?.unlock_inputs?.data.map((inputId) =>
    unlockInputIdsArray.push(inputId)
  );
  return (
    <Page id="UnlockableContentDetail" title={unlockableContentName}>
      <ScrollToTop />
      <Container>
        <h1>{unlockableContentName}</h1>
        <div className="d-flex flex-column">
          {unlockableContent?.public_medium_original &&
            unlockableContent?.public_medium_metadata && (
              <Medium
                mediumData={{
                  ...unlockableContent?.public_medium_original,
                  ...unlockableContent?.public_medium_metadata,
                }}
                className="justify-content-center mb-3"
                altTitle={unlockableContent?.title}
                skipLazyLoading
              />
            )}
          <div className="mb-3">
            {contentCategory?.name && (
              <Badge color="primary" pill className="me-1 mb-3">
                {contentCategory.name}
              </Badge>
            )}
            {unlockableContent?.content_type && (
              <Badge color="primary" pill className="me-1 mb-3">
                {unlockableContent.content_type}
              </Badge>
            )}
            {contentId === REACT_APP_UNLOCKABLE_CONTENT_ID && (
              <Row>
                <Col xs={12} md={6}>
                  <ReactPlayer
                    width="100%"
                    height="100%"
                    controls={true}
                    playing={true}
                    muted={true}
                    loop={true}
                    url="https://firebasestorage.googleapis.com/v0/b/an-nft.appspot.com/o/content%2FGOLDEN_NFG_small.mp4?alt=media&token=4def0b49-1fd9-42c5-8cf4-74a14587b917"
                  />
                </Col>
                <Col xs={12} md={6}>
                  {unlockableContent?.public_description && (
                    <div
                      className="mb-3"
                      dangerouslySetInnerHTML={{
                        __html: unlockableContent.public_description,
                      }}
                    />
                  )}
                </Col>
              </Row>
            )}
            {contentId != REACT_APP_UNLOCKABLE_CONTENT_ID &&
              unlockableContent?.public_description && (
                <div
                  className="mb-3"
                  dangerouslySetInnerHTML={{
                    __html: unlockableContent.public_description,
                  }}
                />
              )}
            {/* TODO: If user has only one token for this project skip selection */}

            <Button
              onClick={() => {
                toggleTokenSelector(true);
              }}
              color="primary"
              className="my-3 w-100"
            >
              {context.t("Unlock")}
            </Button>

            {tokenSelectorOpen && (
              <UnlockTokenSelector
                isOpen={tokenSelectorOpen}
                toggleModal={toggleTokenSelector}
                unlockableContentId={contentId}
                confirmUnlockFeedbackFunction={unlockContent}
                unlockInputIdsArray={unlockInputIdsArray}
              />
            )}
            {unlockableContent?.secret_description && (
              <div className="mt-3">
                <hr />
                <p className="h4">{context.t("Secret Description")}</p>
                <div
                  className="mb-3"
                  dangerouslySetInnerHTML={{
                    __html: unlockableContent.secret_description,
                  }}
                />
              </div>
            )}
            {unlockableContent?.secret_files &&
              unlockableContent?.secret_files.length > 0 && (
                <div className="mt-3">
                  <hr />
                  <p className="h4">{context.t("Secret Files")}</p>
                  {unlockableContent?.secret_files.map((mediumData, index) => (
                    <Medium key={index} mediumData={mediumData} />
                  ))}
                </div>
              )}
          </div>
        </div>
        {error && (
          <ErrorModal
            error={error}
            errorReset={showUnlockableContentErrorReset}
            customErrorBody={
              "There was a problem showing this unlockable content. Please contact support on discord"
            }
          />
        )}
        {unlockError && (
          <ErrorModal
            error={unlockError?.attributes?.full_error_message}
            errorReset={showUnlockableContentErrorReset}
            customErrorBody={
              "There was a problem unlocking this unlockable content. Please contact support on discord"
            }
          />
        )}
        {unlockSuccess && (
          <SuccessModal
            success
            reset={showUnlockableContentErrorReset}
            successMessage={context.t("Successfully Unlocked!")}
          />
        )}
      </Container>
    </Page>
  );
};

/**  define proptype for the 'translation' function  */
UnlockableContentDetail.contextTypes = {
  t: PropTypes.func,
};

/** export the component as redux connected component */
export default UnlockableContentDetail;
